import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Switch, Space, Popconfirm, message, Upload, Image, Radio } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, LinkOutlined } from '@ant-design/icons';
import request from '../../utils/api';

const API_HOST = process.env.REACT_APP_API_HOST || '';

export default function Banners() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [imageMode, setImageMode] = useState('upload');
  const [form] = Form.useForm();

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const res = await request.get('/banners/admin/list');
      setData(res.data);
    } catch (e) {} finally { setLoading(false); }
  }

  // 根据 imageUrl 判断是上传文件还是外部 URL
  function detectImageMode(url) {
    if (!url) return 'upload';
    return url.startsWith('/uploads/') ? 'upload' : 'url';
  }

  function openCreate() {
    form.resetFields();
    setPreviewUrl('');
    setImageMode('upload');
    setModal({ open: true, editing: null });
  }

  function openEdit(record) {
    form.setFieldsValue(record);
    const mode = detectImageMode(record.imageUrl);
    setImageMode(mode);
    setPreviewUrl(getFullUrl(record.imageUrl));
    setModal({ open: true, editing: record });
  }

  // 获取完整预览 URL
  function getFullUrl(url) {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads/')) return API_HOST + url;
    return url;
  }

  async function handleUpload(file) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await request.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data.url;
      form.setFieldsValue({ imageUrl: url });
      setPreviewUrl(getFullUrl(url));
      message.success('上传成功');
    } catch (e) {
      message.error('上传失败');
    } finally {
      setUploading(false);
    }
    return false;
  }

  // 切换图片来源模式
  function handleModeChange(e) {
    const mode = e.target.value;
    setImageMode(mode);
    form.setFieldsValue({ imageUrl: '' });
    setPreviewUrl('');
  }

  // URL 输入变化时更新预览
  function handleUrlChange(e) {
    const url = e.target.value;
    setPreviewUrl(url);
  }

  async function handleSave() {
    try {
      const values = await form.validateFields();
      if (!values.imageUrl) {
        message.error('请上传图片或填写图片URL');
        return;
      }
      if (modal.editing) {
        await request.put(`/banners/${modal.editing.id}`, values);
        message.success('更新成功');
      } else {
        await request.post('/banners', values);
        message.success('添加成功');
      }
      setModal({ open: false, editing: null });
      setPreviewUrl('');
      loadData();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  }

  async function handleDelete(id) {
    try {
      await request.delete(`/banners/${id}`);
      message.success('已删除');
      loadData();
    } catch (e) { message.error('删除失败'); }
  }

  const columns = [
    { title: '排序', dataIndex: 'sortOrder', width: 80 },
    { title: '标题', dataIndex: 'title' },
    { title: '预览', dataIndex: 'imageUrl', width: 120,
      render: v => v ? <Image src={getFullUrl(v)} width={80} height={50} style={{ objectFit: 'cover', borderRadius: 4 }} /> : <span style={{ color: '#ccc' }}>无图片</span>
    },
    { title: '图片来源', dataIndex: 'imageUrl', width: 100,
      render: v => v ? (v.startsWith('/uploads/') ? <span style={{ color: '#1890ff' }}>本地上传</span> : <span style={{ color: '#52c41a' }}>外部URL</span>) : '-'
    },
    { title: '跳转链接', dataIndex: 'linkUrl', ellipsis: true },
    { title: '状态', dataIndex: 'isActive', render: v => v ? <span style={{ color: 'green' }}>启用</span> : <span style={{ color: '#ccc' }}>禁用</span> },
    { title: '操作', key: 'action', width: 180,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>轮播广告管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>添加轮播图</Button>
      </div>
      <Table columns={columns} dataSource={data} rowKey="id" loading={loading} pagination={false} />

      <Modal title={modal.editing ? '编辑轮播图' : '添加轮播图'} open={modal.open}
        onOk={handleSave} onCancel={() => { setModal({ open: false, editing: null }); setPreviewUrl(''); }}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true }]}>
            <Input placeholder="如：夏日露营季" />
          </Form.Item>

          <Form.Item label="图片来源" required>
            <Radio.Group value={imageMode} onChange={handleModeChange} style={{ marginBottom: 12 }}>
              <Radio.Button value="upload">
                <UploadOutlined /> 本地上传
              </Radio.Button>
              <Radio.Button value="url">
                <LinkOutlined /> 填写URL
              </Radio.Button>
            </Radio.Group>

            {imageMode === 'upload' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Upload beforeUpload={handleUpload} showUploadList={false} accept="image/*">
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    {uploading ? '上传中...' : '选择图片上传'}
                  </Button>
                </Upload>
                <div style={{ fontSize: 12, color: '#999' }}>建议尺寸 750x375px，比例 2:1，JPG/PNG，不超过 10MB</div>
              </div>
            ) : (
              <Form.Item name="imageUrl" noStyle rules={[{ required: true, message: '请输入图片URL' }]}>
                <Input placeholder="https://example.com/banner.jpg" onChange={handleUrlChange} />
              </Form.Item>
            )}
          </Form.Item>

          {imageMode === 'upload' && (
            <Form.Item name="imageUrl" hidden>
              <Input />
            </Form.Item>
          )}

          <Form.Item label="预览">
            {previewUrl ? (
              <div>
                <Image src={previewUrl} width="100%" style={{ maxWidth: 375, borderRadius: 8, border: '1px solid #eee' }} />
                <div style={{ marginTop: 4, fontSize: 12, color: '#999', wordBreak: 'break-all' }}>
                  {previewUrl}
                </div>
              </div>
            ) : (
              <div style={{ padding: 30, background: '#fafafa', borderRadius: 8, textAlign: 'center', color: '#999', border: '1px dashed #d9d9d9' }}>
                <div style={{ fontSize: 14, marginBottom: 8 }}>
                  {imageMode === 'upload' ? '点击上方按钮上传轮播图片' : '输入图片URL后自动预览'}
                </div>
                <div style={{ fontSize: 12 }}>推荐尺寸 750x375px，比例 2:1</div>
              </div>
            )}
          </Form.Item>

          <Form.Item name="linkUrl" label="跳转链接">
            <Input placeholder="可选，点击轮播图跳转的页面链接" />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序（越小越靠前）">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" label="是否启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
